import React, {useCallback, useEffect, useRef, useState} from 'react';
import treauApiClient, {
  TreauAPI
} from '../../../../api_clients/TreauApiClient';

import Button from '../../../../components/Button';
import LoadingSpinner from '../../../../components/LoadingSpinner';
import ConfirmationModal from '../../../../components/Modal/ConfirmationModal';

import {useToastAPIHandler} from '../../../../hooks/api';

import {AxiosResponse} from 'axios';

import './index.scss';

interface CurrentLimitFormProps {
  unid: string;
  thingName: string;
}

const REFRESH_INTERVAL_SECONDS = 5;

const LIMIT_LABELS: Record<TreauAPI.AdminUpdateCurrentLimitInputCurrentLimitEnum, string> = {
  [TreauAPI.AdminUpdateCurrentLimitInputCurrentLimitEnum.SevenPointFiveAmp]: '7.5 Amps',
  [TreauAPI.AdminUpdateCurrentLimitInputCurrentLimitEnum.TwelveAmp]: '12 Amps',
};

const LIMIT_OPTIONS = [
  {
    value: TreauAPI.AdminUpdateCurrentLimitInputCurrentLimitEnum.SevenPointFiveAmp,
    label: LIMIT_LABELS[TreauAPI.AdminUpdateCurrentLimitInputCurrentLimitEnum.SevenPointFiveAmp],
  },
  {
    value: TreauAPI.AdminUpdateCurrentLimitInputCurrentLimitEnum.TwelveAmp,
    label: LIMIT_LABELS[TreauAPI.AdminUpdateCurrentLimitInputCurrentLimitEnum.TwelveAmp],
  },
];

const CurrentLimitForm = ({unid, thingName}: CurrentLimitFormProps) => {
  const apiHandler = useToastAPIHandler();

  const [loading, setLoading] = useState(true);
  const pollingRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const postSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [currentLimitInfo, setCurrentLimitInfo] = useState<TreauAPI.ResponsesAdminGetCurrentLimitShadowResponseData>({
    desiredCurrentLimit: TreauAPI.ResponsesAdminGetCurrentLimitShadowResponseDataDesiredCurrentLimitEnum.TwelveAmp,
    reportedCurrentLimit: TreauAPI.ResponsesAdminGetCurrentLimitShadowResponseDataReportedCurrentLimitEnum.TwelveAmp,
  });
  const [selectedLimit, setSelectedLimit] = useState<TreauAPI.AdminUpdateCurrentLimitInputCurrentLimitEnum | null>(null);
  const [desiredMatchesReported, setDesiredMatchesReported] = useState(true);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [hasDoubleCheckedShadowAfterChange, setHasDoubleCheckedShadowAfterChange] = useState(false);

  const fetchCurrentLimitInfo = useCallback(() => {
    setLoading(true);
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
    }
    apiHandler.handleRequest(
      treauApiClient.adminsAPI.adminGetCurrentLimit(unid),
      (response: AxiosResponse<TreauAPI.ResponsesAdminGetCurrentLimitShadowResponse>) => {
        const data = response.data.data;
        const desired = data.desiredCurrentLimit as string;
        const reported = data.reportedCurrentLimit as string;
        if (desired === reported) {
          if (selectedLimit && (selectedLimit as string) !== reported && !hasDoubleCheckedShadowAfterChange) {
            // device removes the desired section from shadow before updating to the new value, check one more time
            setHasDoubleCheckedShadowAfterChange(true);
            setTimeout(() => {
              fetchCurrentLimitInfoRef.current();
            }, REFRESH_INTERVAL_SECONDS * 1000);
          } else {
            setLoading(false);
            setCurrentLimitInfo(data);
            setSelectedLimit(data.desiredCurrentLimit as TreauAPI.AdminUpdateCurrentLimitInputCurrentLimitEnum);
            setDesiredMatchesReported(desired === reported);
          }
        } else {
          setLoading(false);
          pollingRef.current = setTimeout(() => {
            fetchCurrentLimitInfoRef.current();
          }, REFRESH_INTERVAL_SECONDS * 1000);
        }
      },
      () => setLoading(false),
      undefined
    );
  }, [apiHandler, unid, selectedLimit, hasDoubleCheckedShadowAfterChange]);
  const fetchCurrentLimitInfoRef = useRef(fetchCurrentLimitInfo);

  const updateCurrentLimit = () => {
    if (!selectedLimit) {
      return;
    }
    setShowConfirmationModal(false);
    setLoading(true);
    const input: TreauAPI.AdminUpdateCurrentLimitInput = {
      currentLimit: selectedLimit,
    };
    apiHandler.handleRequest(
      treauApiClient.adminsAPI.adminUpdateCurrentLimit(unid, input),
      () => {
        if (postSaveTimeoutRef.current) {
          clearTimeout(postSaveTimeoutRef.current);
        }
        postSaveTimeoutRef.current = setTimeout(fetchCurrentLimitInfo, 1500);
      },
      () => setLoading(false),
      undefined
    );
  };

  useEffect(() => {
    fetchCurrentLimitInfoRef.current();
    return () => {
      if (pollingRef.current) {
        clearTimeout(pollingRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSaveClick = () => {
    if (!selectedLimit) {
      return;
    }
    setShowConfirmationModal(true);
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div
          className="current-limit-form d-flex flex-column align-items-center p-5">
          <LoadingSpinner/>
        </div>
      );
    }

    const reportedLimit = LIMIT_LABELS[
      currentLimitInfo.reportedCurrentLimit as unknown as TreauAPI.AdminUpdateCurrentLimitInputCurrentLimitEnum
      ];
    const desiredLimit = LIMIT_LABELS[
      currentLimitInfo.desiredCurrentLimit as unknown as TreauAPI.AdminUpdateCurrentLimitInputCurrentLimitEnum
      ];
    const hasChangePending =
      selectedLimit !== (currentLimitInfo.desiredCurrentLimit as TreauAPI.AdminUpdateCurrentLimitInputCurrentLimitEnum);

    return (
      <div
        className="current-limit-form d-flex flex-column align-items-center p-4">
        {
          desiredMatchesReported ? <>
            <p className="text-center">Current
              limit: <strong>{reportedLimit}</strong></p>
            <div
              className="current-limit-toggle d-flex justify-content-center mt-3">
              {LIMIT_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`limit-toggle-option ${selectedLimit === option.value ? 'active' : ''}`}
                  onClick={() => setSelectedLimit(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <Button
              className="mt-4 text-uppercase"
              disabled={!selectedLimit || !hasChangePending}
              onClick={handleSaveClick}
            >
              Save
            </Button>
          </> : <>
            <p className="text-center">The current limit has been set
              to {desiredLimit} but the device has not yet acknowledged the
              command.</p>
            <p className="text-center mt-3">This display will refresh
              every {REFRESH_INTERVAL_SECONDS} seconds.</p>
          </>
        }
      </div>
    );
  };

  return (
    <>
      {renderContent()}
      <ConfirmationModal
        show={showConfirmationModal}
        handleClose={() => setShowConfirmationModal(false)}
        onConfirm={updateCurrentLimit}
        message={`Are you sure you want to set the current limit to  ${LIMIT_LABELS[selectedLimit]} for device ${thingName}?`}
      />
    </>
  );
};

export default CurrentLimitForm;
